<template>
  <div class="editor-outer-container">
    <label v-if="labelText">
      {{ labelText }}
      <a :href="helpLink" v-if="helpLink"><i class="circle help icon"></i></a>
    </label>

    <div class="editor-inner-container">
      <div id="editor" ref="editor" :style="{ height: height }" class="no-short-keys"></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
.editor-outer-container {
  margin-top: 1rem;
}

.editor-inner-container {
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  margin-bottom: 10px;

  &:focus-within {
    border: 1px solid #85b7d9;
  }
}
#editor {
  margin: 10px;
}

label {
  display: inline-block;
  font-weight: bold;
  margin-bottom: 3px;
  font-size: 0.9em;
}
</style>

<script>
// @flow
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api' // needs to be imported this way in order to include only subset of languages
import _ from 'lodash'

// from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export default {
  name: 'estero-editor',

  props: ['value', 'height', 'operators', 'properties', 'labelText', 'helpLink'],

  data() {
    return {
      editor: null
    }
  },

  methods: {
    init() {
      if (this.editor) return

      console.debug('inited editor', this.$refs.editor)

      // Theme
      monaco.editor.defineTheme('EsteroEditorTheme', {
        base: 'vs',
        inherit: true,
        rules: [
          { token: 'KEYWORD', fontStyle: 'bold' },
          // operators are bold and purple
          { token: 'OPERATOR', fontStyle: 'bold', foreground: '800080' },
          // properties are blue
          { token: 'PROPERTY', foreground: '0000ff' },
          { token: 'STRING', foreground: '00aa00' },
          { token: 'NUMBER', foreground: 'aa0000' },
          { token: 'BOOLEAN', foreground: '0000aa' }
        ],

        colors: {
          'editor.lineHighlightBackground': '#FFFFFF',
          'editorLineNumber.foreground': '#008800',
          'editorWarning.foreground': '#FFCC00',
          'editorInfo.foreground': '#FFCC00'
        }
      })

      // Register a new language
      monaco.languages.register({ id: 'mql' })

      // Register a tokens provider for the language
      monaco.languages.setMonarchTokensProvider('mql', {
        tokenizer: {
          root: [
            [/(SELECT|AS|WHERE|AND|OR|NOT)/, 'KEYWORD'],
            ..._.map(_.values(this.operators), op => [escapeRegExp(op.symbol), 'OPERATOR']),
            ..._.map(this.properties, prop => [escapeRegExp(_.upperCase(prop)), 'PROPERTY']),
            ..._.map(this.properties, prop => [escapeRegExp(_.lowerCase(prop)), 'PROPERTY']),
            [/\"[^\"]*\"/, 'STRING'],
            [/\'[^\']*\'/, 'STRING'],
            [/\d+/, 'NUMBER'],
            [/(true|false|TRUE|FALSE)/, 'BOOLEAN']
          ]
        }
      })

      // Register a completion item provider for the new language
      monaco.languages.registerCompletionItemProvider('mql', {
        provideCompletionItems: () => {
          var suggestions = [
            {
              label: 'SELECT',
              kind: monaco.languages.CompletionItemKind.Text,
              insertText: 'SELECT',
              documentation: this.$i18n.t('SELECT_DOCUMENTATION')
            },
            {
              label: 'AS',
              kind: monaco.languages.CompletionItemKind.Text,
              insertText: 'AS',
              documentation: this.$i18n.t('AS_DOCUMENTATION')
            },
            {
              label: 'WHERE',
              kind: monaco.languages.CompletionItemKind.Text,
              insertText: 'WHERE',
              documentation: this.$i18n.t('WHERE_DOCUMENTATION')
            },
            {
              label: 'SELECT with WHERE clause',
              kind: monaco.languages.CompletionItemKind.Snippet,
              insertText: 'SELECT ${1:properties} WHERE ${2:clause}',
              insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              documentation: this.$i18n.t('SELECT_WHERE_DOCUMENTATION')
            },
            ..._.map(this.properties, p => ({
              label: p,
              kind: monaco.languages.CompletionItemKind.Text,
              insertText: p
            }))
          ]
          return { suggestions }
        }
      })

      this.editor = monaco.editor.create(this.$refs.editor, {
        value: this.value,
        language: 'mql',
        tabSize: 2,
        codeLens: false,
        theme: 'EsteroEditorTheme',
        glyphMargin: false,
        lineNumbers: 'off',
        wordWrap: 'on',
        folding: false,
        // Undocumented see https://github.com/Microsoft/vscode/issues/30795#issuecomment-410998882
        lineDecorationsWidth: 0,
        lineNumbersMinChars: 0,
        scrollbar: { vertical: 'hidden', verticalScrollbarSize: 0 },
        minimap: { enabled: false }
      })

      this.editor.onDidChangeModelContent(() => {
        this.$emit('contentchanged', this.editor.getValue())
      })

      // Auto-format
      this.editor.updateOptions({
        autoIndent: true,
        formatOnPaste: true,
        formatOnType: true
      })

      // Ctrl/Cmd+Enter to run query
      this.editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
        this.$emit('run-query')
      })

      // Compiler options
      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ES5,
        noLib: true,
        allowNonTsExtensions: true
      })

      monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
        noLib: true,
        allowNonTsExtensions: true
      })

      // HACK to get the vue-shortkey plugin to avoid catching ctrl/cmd+enter
      document.querySelector('#editor textarea').classList.add('no-short-keys')
    }
  },

  mounted() {
    this.init()
  },

  watch: {
    value(v) {
      if (this.editor?.getValue() != v) this.editor?.setValue(v)
    }
  }
}
</script>
